import { Store } from '../store.class'
import { store } from '@/store'
import router from '@/router'
import { toast } from '@/api/tools'

import * as Tripply from '@/api/tripply'
import { Route, Routes, RouteSettings, Place, PlaceModel, Location } from '@/api/tripply/models'
import { authStore } from './auth.store'
import { GA } from '@/api/analytics'

const saveDebounce = 5000


interface RouteState {
    routes: Routes;
    debounces: {[routeId: string]: NodeJS.Timeout};
}

class RouteStore extends Store<RouteState> {
  constructor() {
    super({
      name: 'route',
      cached: false,
      state: {
        routes: {},
        debounces: {}
      }
    })
  }

  // Mutators
  SET_LOADING(routeId: string, loading: boolean) {
    if (this.state.routes[routeId]) this.state.routes[routeId].loading = loading
  }
  SET_ROUTE(routeId: string, route: Route) {  
    console.log('ROUTE UPDATED')
      
    if (store.user.isOwner(route) && !store.user.permission('FEATURE_PRIVATE_ROUTES')) {
      route.settings.private = false
    }
    this.state.routes[routeId] = route
    this.debounceSave(route)
  }
  SET_LIVE(routeId: string, live: boolean) {
    if (this.state.routes[routeId]) this.state.routes[routeId].live = live
  }
  DELETE_ROUTE(routeId: string) {
    delete this.state.routes[routeId]
  }
  SET_PLACES(routeId: string, places: Place[]) {
    this.state.routes[routeId].places = places
  }

  // Getters
  route(routeId: string) { return this.get(state => state.routes[routeId]) }
  getRoute(routeId: string): Route {
    return this.state.routes[routeId]
  }
  getPlaces(routeId: string): Place[] {
    return this.getRoute(routeId).places
  }
  getUserRoutes = (userId: string | undefined): Route[] => {
    return userId ?
      [...Object.values(this.state.routes).filter(route => route.roles.owner === userId)]
      : []
  }

  // Actions
  debounceSave(route: Route) {
    if (!route.saved) {
      clearTimeout(this.state.debounces[route.id])
      this.mutate(store => store.debounces[route.id] = setTimeout(() => {
        this.saveRoute(route.id)
      }, saveDebounce))
    } else {
      clearTimeout(this.state.debounces[route.id])
    }
  }
  createRoute(routeSettings: RouteSettings, errors?: boolean) {
    const userId = authStore.getUserId()
    if (userId) {
      try {
        const route = Tripply.createRoute(userId, routeSettings)
        this.SET_ROUTE(route.id, route)
        return route.id
      } catch(err) {
        if (errors) throw err
        toast({
          title: err,
          type: 'warning'
        })
      }
    }
  }
  async deleteRoute(routeId: string) {
    this.DELETE_ROUTE(routeId)
    await Tripply.deleteRoute(routeId)
    router.push('/routes')
    // TODO: Re-set route if delete fails or similar...
  }

  async initialize(routeId: string) {
    const route = this.getRoute(routeId)
    if (!route) {
      // If route is not in local store
      this.SET_LOADING(routeId, true)
      try {
        // Get route from database
        const nextRoute = await Tripply.fetchRoute(routeId)
        this.SET_ROUTE(routeId, nextRoute)
      } catch(err) {
        console.log(err)
        throw new Error('Route does not exist')
      } finally {
        this.SET_LOADING(routeId, false)
      }
    } else if (route.places.length >= 2 && !route.geometry.legs.length) {
      // If route exists locally, but geometry is missing
      try {
        const geometry = await Tripply.fetchRouteGeometry(routeId)
        const nextRoute = { ...route, geometry: geometry } 
        this.SET_ROUTE(routeId, nextRoute)
      } catch(err) {
        console.log(err)
      }
    }
  }

  async saveRoute(routeId: string) {
    if (this.getRoute(routeId).saved) return
    console.log('ROUTE SAVED', routeId)
    clearTimeout(this.state.debounces[routeId])
    const nextRoute = await Tripply.saveRouteById(routeId)    
    nextRoute && this.SET_ROUTE(routeId, nextRoute)
    return
  }
  async changePlaceIndex(routeId: string, placeIndex: number, newIndex: number) {
    this.SET_LOADING(routeId, true)
    try {      
      const nextRoute = await Tripply.changePlaceIndex(routeId, placeIndex, newIndex)
      nextRoute && this.SET_ROUTE(routeId, nextRoute)
    } catch(err) {
      toast({
        title: 'Route cannot be manually modified ',
        msg: 'If you want to manually change the order of your stops, please enable the manual calculation method in your route settings.',
        type: 'warning'
      })
    } finally {
      this.SET_LOADING(routeId, false)
    }
  }
  async setPlaceFirst(routeId: string, placeIndex: number) {
    this.SET_LOADING(routeId, true)
    const nextRoute = await Tripply.setPlaceFirst(routeId, placeIndex)    
    nextRoute && this.SET_ROUTE(routeId, nextRoute)
    this.SET_LOADING(routeId, false)
  }
  async setPlaceLast(routeId: string, placeIndex: number) {
    this.SET_LOADING(routeId, true)
    const nextRoute = await Tripply.setPlaceLast(routeId, placeIndex)
    nextRoute && this.SET_ROUTE(routeId, nextRoute)
    this.SET_LOADING(routeId, false)
  }
  async addLocation(routeId: string, location: Location) {
    const place = new PlaceModel(location)
    return this.addPlace(routeId, place)
  }
  async addPlace(routeId: string, place: Place) {  
    return this.addPlaces(routeId, [place])
  }
  async addPlaces(routeId: string, places: Place[]) {
    this.SET_LOADING(routeId, true)
    try {
      const nextRoute = await Tripply.addPlaces(routeId, places)
      nextRoute && this.SET_ROUTE(routeId, nextRoute)
    } catch(err) {
      toast({
        title: err,
        type: 'warning'
      })
    } finally {
      this.SET_LOADING(routeId, false)
    }
  }
  async deletePlace(routeId: string, placeIndex: number) {
    this.SET_LOADING(routeId, true)
    const nextRoute = await Tripply.deletePlace(routeId, placeIndex)
    nextRoute && this.SET_ROUTE(routeId, nextRoute)
    this.SET_LOADING(routeId, false)
  }
  async updatePlace(routeId: string, placeIndex: number, updatedPlace: Partial<Place>) {
    this.SET_LOADING(routeId, true)
    const nextRoute = await Tripply.updatePlace(routeId, placeIndex, updatedPlace)
    nextRoute && this.SET_ROUTE(routeId, nextRoute)
    this.SET_LOADING(routeId, false)
  }
  exportRoute(routeId: string) {
    Tripply.exportRoute(routeId)
  }
  async updateSettings(routeId: string, settings: Partial<RouteSettings>, save?: boolean) {
    console.log('NEW SETTINGS', settings)
    this.SET_LOADING(routeId, true)
    try {
      const nextRoute = await Tripply.updateSettings(routeId, settings, save)
      nextRoute && this.SET_ROUTE(routeId, nextRoute)
    } catch(err) {
      toast({title: err})
    } finally {
      this.SET_LOADING(routeId, false)
    }
  }

  placeLimitExceeded(routeId: string) {
    return this.getPlaces(routeId).length > store.user.permission('LIMIT_ROUTE_PLACES')
  }

  share(routeId: string) {
    const route = this.getRoute(routeId)
    if (navigator.share) {
      navigator.share({
        title: route.settings.name + ' - tripply.dk',
        text: 'Her er et link til min rute: ' + route.settings.name,
        url: Tripply.getRouteLink(routeId)
      })
    } else {
      store.modal.showModal('ShareRoute', {route: route})
    }
  }

  // Live Actions
  async goLive(routeId: string) {
    this.SET_LOADING(routeId, true)
    try {
      store.ui.mutate(state => state.route.settingsDrawer = false)
      const nextRoute = await Tripply.goLive(routeId)
      nextRoute && this.SET_ROUTE(routeId, nextRoute)
      GA('live_start')
    }
    catch(err) {
      store.modal.showModal('PayWall', {feature: 'LIVE_MODE'})
      GA('live_start_attempt')
    }
    finally {
      this.SET_LOADING(routeId, false)
    }
  }
  async endLive(routeId: string) {
    if (store.user.isOwner(routeId)) {
      this.SET_LOADING(routeId, true)
      this.SET_LIVE(routeId, false)
      const nextRoute = await Tripply.endLive(routeId)
      nextRoute && this.SET_ROUTE(routeId, nextRoute)
      store.map.updateGeometry(nextRoute.geometry)
      store.live.terminate(routeId)
      this.SET_LOADING(routeId, false)
      GA('live_end')
    }
  }
}
export const routeStore = new RouteStore()